"use client";

import { useQuery } from "@apollo/client/react";
import { useUser } from "@auth0/nextjs-auth0/client";
import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import Image from "next/image";
import React, { createContext, useContext, useState } from "react";

import { graphql } from "../../__generated__/gql";
import { Menu } from "../_components/global-menu/menu";
import { MenuCloseIcon } from "../_components/icons/menu-close-icon";
import { Link } from "../_components/ui/link";
import { useIsOrganizationManager } from "../_hooks/use-is-organization-manager";
import { useIsMobile } from "../_hooks/use-responsive-value";

const Query = graphql(`
  query AreasForGlobalMenu {
    areas {
      id
      name
      slug
      sites {
        id
      }
    }
  }
`);

export const GlobalMenuContext = createContext<{
  isLoggedIn: boolean;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
}>({
  isLoggedIn: false,
  isMenuOpen: false,
  setIsMenuOpen: () => {},
});

export default function GlobalMenu() {
  const { user, isLoading } = useUser();
  const isLoggedIn = !!user;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: areasData } = useQuery(Query);
  // 型上は non-null だが、bingbot のレンダラーでは areas を欠いたレスポンスが返り落ちるため空扱いにする。
  // メニューは開くまで HTML に出ず SSR しても SEO 上の効果が無いのでクライアント取得のままにしている。公式サイト刷新時に再検討する。
  const areas = areasData?.areas ?? [];

  const { isOrganizationManager } = useIsOrganizationManager();

  return (
    <GlobalMenuContext.Provider
      value={{ isLoggedIn, isMenuOpen, setIsMenuOpen }}
    >
      <button
        data-analytics-id="global_menu_opened_menu"
        className={clsx(
          "flex h-full w-[60px] items-center justify-center md:w-[100px]",
          isLoading ? "cursor-progress opacity-20" : "hover:opacity-60",
        )}
        onClick={() => setIsMenuOpen(true)}
        disabled={isLoading}
      >
        {isMenuOpen ? (
          <MenuCloseIcon classname="w-[24px] h-[24px] fill-skogecha stroke-skogecha" />
        ) : (
          <Image src={"/i/menu_icon.svg"} alt="menu" width={24} height={20} />
        )}
      </button>
      <GlobalMenuDialog
        areas={areas.filter((area) => area.sites.length > 0)}
        showOrganizationManagerMenu={isOrganizationManager}
      />
    </GlobalMenuContext.Provider>
  );
}

function GlobalMenuDialog({
  areas,
  showOrganizationManagerMenu = false,
}: {
  areas: { id: string; name: string; slug: string }[];
  showOrganizationManagerMenu?: boolean;
}) {
  const { isLoggedIn, isMenuOpen, setIsMenuOpen } =
    useContext(GlobalMenuContext);

  const isMobile = useIsMobile();

  return (
    <Dialog.Root open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <Dialog.Portal>
        <Dialog.Content
          className={clsx(
            "fixed top-0 left-0 z-100 flex-row justify-stretch drop-shadow-xl",
            {
              "hidden w-dvw bg-white md:flex": !isMobile,
              "bg-skogecha mt-[60px] flex h-[calc(100dvh-60px)] w-full overflow-y-auto md:hidden":
                isMobile,
            },
          )}
          aria-describedby={undefined}
        >
          <Dialog.Title className="hidden">メニュー</Dialog.Title>
          {!isMobile && (
            <div className="flex w-[320px] flex-auto justify-start pt-[30px] pl-[40px]">
              <Link
                data-analytics-id="global_menu_clicked_logo"
                href={isLoggedIn ? "/member" : "/"}
                onClick={() => setIsMenuOpen(false)}
                className="shrink-0"
              >
                <Image
                  src="/i/sanu2ndhome_logo.svg"
                  alt="SANU 2nd Home Logo"
                  width={129}
                  height={19}
                />
              </Link>
            </div>
          )}
          <Menu
            areas={areas}
            isMobile={isMobile}
            isLoggedIn={isLoggedIn}
            showOrganizationManagerMenu={showOrganizationManagerMenu}
          />
          {!isMobile && (
            <div className="flex w-[320px] flex-auto flex-row justify-end bg-[#332f21]">
              <button
                // 閉じるだけで、押されなかったことに情報が無い
                data-analytics-ignore
                className="flex h-[80px] w-[100px] items-center justify-center"
                onClick={() => setIsMenuOpen(false)}
              >
                <MenuCloseIcon classname="w-[24px] h-[24px] fill-white stroke-white" />
              </button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
